/**
 * TemplePujaCheckout — leaner 2-step booking modal for Temple Puja
 * (participant-count packages, no cart/address/coupon — devotees give
 * name+gotra instead). Sibling of InlineCheckout.jsx, same UX language.
 *
 * Step 1: Devotee Details (+ phone OTP verify for guests)
 * Step 2: Payment (booking summary + Razorpay)
 */
import { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import toast from "react-hot-toast";
import { X, Check, ChevronLeft, Shield, Phone, Plus, Calendar, Clock, Users } from "lucide-react";

import { getUserDetails, sendPhoneOTP, verifyPhoneOTP, createTemplePujaOrder, verifyTemplePujaPayment } from "@/pages/api";
import { displayRazorpay, getCookieItem, setCookieItem } from "@/utils/utils";

const ADMIN_PHONE = "919869929292";
const TIER_LABELS = {
  individual: "Individual",
  couple: "Couple",
  family: "Family",
  large_family: "Large Family",
  custom: "Custom",
};

const ContactAdmin = () => (
  <a
    href={`tel:+${ADMIN_PHONE}`}
    className="flex items-center justify-center gap-1.5 text-xs text-gray-400 hover:text-[#7B1520] transition-colors mt-1"
  >
    <Phone size={11} />
    Need help? Call us
  </a>
);

const emptyDevotee = () => ({ name: "", gotra: "", age: "", relation: "", showMore: false });

export default function TemplePujaCheckout({
  templePujaId,
  templePujaName,
  templePujaPackageId,
  packageType,
  participantCount,
  templePujaDateSlotId,
  bookingDate,
  bookingTimeSlotText,
  amount,
  onClose,
}) {
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // ── User / phone verify ──
  const [userData, setUserData] = useState({});
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  // ── Devotees ──
  const [devotees, setDevotees] = useState(
    Array.from({ length: Math.max(participantCount, 1) }, emptyDevotee)
  );
  const [extraDetailsByUser, setExtraDetailsByUser] = useState("");

  const fetchUser = async () => {
    const res = await getUserDetails();
    if (res?.data?.statusCode === 200) {
      const u = res.data.body;
      setUserData(u);
      setPhoneVerified(!!(u?.phone && u?.isPhoneVerified));
      return u;
    }
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDevotees((prev) => {
      const count = Math.max(participantCount, 1);
      if (prev.length === count) return prev;
      if (prev.length < count) {
        return [...prev, ...Array.from({ length: count - prev.length }, emptyDevotee)];
      }
      return prev.slice(0, count);
    });
  }, [participantCount]);

  const needsVerify = userData?.userType === 3 && !phoneVerified;

  const handleSendOTP = async () => {
    if (phone.length !== 10) { toast.error("Enter a valid 10-digit number"); return; }
    setLoading(true);
    const res = await sendPhoneOTP(`+91-${phone}`);
    if (res?.status === 201) { setOtpSent(true); toast.success("OTP sent!"); }
    else toast.error("Failed to send OTP");
    setLoading(false);
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    const token = getCookieItem("accessToken");
    const res = await verifyPhoneOTP(`+91-${phone}`, otp, token, undefined, userData?._id);
    if (res?.data?.statusCode === 200) {
      setCookieItem("accessToken", res.data.body.accessToken);
      setPhoneVerified(true);
      await fetchUser();
      toast.success("Phone verified!");
    } else toast.error(res?.data?.message || "Invalid OTP");
    setLoading(false);
  };

  const updateDevotee = (index, field, value) => {
    setDevotees((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const devoteesValid = useMemo(
    () => devotees.length === participantCount && devotees.every((d) => d.name?.trim() && d.gotra?.trim()),
    [devotees, participantCount]
  );

  const handleStep1Next = () => {
    if (needsVerify) { toast.error("Please verify your phone first"); return; }
    if (!devoteesValid) { toast.error("Please fill in name and gotra for every participant"); return; }
    setStep(2);
  };

  // ── Place order + pay ──
  const handlePlaceOrder = async () => {
    setLoading(true);
    try {
      const cleanedDevotees = devotees.map((d) => ({
        name: d.name.trim(),
        gotra: d.gotra.trim(),
        ...(d.age ? { age: Number(d.age) } : {}),
        ...(d.relation?.trim() ? { relation: d.relation.trim() } : {}),
      }));

      const payload = {
        templePujaId,
        templePujaPackageId,
        packageType,
        participantCount,
        templePujaDateSlotId,
        devotees: cleanedDevotees,
        ...(extraDetailsByUser.trim() && { extraDetailsByUser: extraDetailsByUser.trim() }),
      };

      let token = getCookieItem("accessToken");
      const executeOrderCall = async (t) => createTemplePujaOrder(payload, t);

      let res;
      try {
        res = await executeOrderCall(token);
      } catch (err) {
        if (err?.response?.status === 401 || err?.status === 401) {
          const { handleGuestLoginFlow } = await import("@/utils/utils");
          const guestCreated = await handleGuestLoginFlow();
          if (guestCreated) {
            token = getCookieItem("accessToken");
            res = await executeOrderCall(token);
          } else {
            throw err;
          }
        } else {
          throw err;
        }
      }

      if (res?.data?.statusCode === 200) {
        const { initiatedRazorPayOrderId, _id: orderId } = res.data.body;
        const paid = await displayRazorpay({
          amount: Math.round(amount * 100),
          razorpayOrderId: initiatedRazorPayOrderId,
          userData,
        });
        const vRes = await verifyTemplePujaPayment(
          paid.razorpay_payment_id,
          initiatedRazorPayOrderId,
          paid.razorpay_signature,
          orderId,
          token
        );
        if (vRes?.data?.statusCode === 200) {
          toast.success("Booking confirmed! 🙏");
          router.push(`/e-pooja/temple/pujas/order/${orderId}`);
        } else {
          toast.error(vRes?.data?.message || "Payment verification failed. Please contact support.");
        }
      } else {
        toast.error(res?.data?.message || "Could not create booking");
      }
    } catch (e) {
      if (e?.error?.code !== "PAYMENT_CANCELLED") toast.error("Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[88vh] overflow-hidden">
        {/* ── Header ── */}
        <div className="bg-gradient-to-r from-[#7B1520] to-[#C0392B] px-5 pt-5 pb-4 flex-shrink-0">
          <div className="w-10 h-1 bg-white/30 rounded-full mx-auto mb-4 sm:hidden" />
          <div className="flex items-start justify-between mb-5">
            <div>
              <p className="text-white/70 text-[10px] font-bold tracking-widest uppercase mb-1">Secure Booking</p>
              <h2 className="text-white font-bold text-base leading-tight">{templePujaName}</h2>
              <p className="text-white/75 text-xs mt-0.5 capitalize">{TIER_LABELS[packageType] || packageType} Package</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-all flex-shrink-0 ml-2">
              <X size={15} />
            </button>
          </div>

          <div className="flex items-center">
            {[["1", "Devotee Details"], ["2", "Payment"]].map(([n, label], i) => (
              <div key={n} className="flex items-center flex-1">
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step > +n ? "bg-white text-[#7B1520]"
                    : step === +n ? "bg-white text-[#7B1520] ring-2 ring-white/40 ring-offset-1 ring-offset-[#9B1F2F]"
                      : "bg-white/20 text-white/50"
                    }`}>
                    {step > +n ? <Check size={12} /> : n}
                  </div>
                  <span className={`text-[9px] font-semibold tracking-wide ${step >= +n ? "text-white" : "text-white/40"}`}>{label}</span>
                </div>
                {i < 1 && <div className={`flex-1 h-px mx-2 mb-4 ${step > +n ? "bg-white" : "bg-white/25"}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto">
          {step === 1 && (
            <div className="px-5 py-5 space-y-5">
              {needsVerify && (
                <div className="rounded-2xl bg-rose-50 border border-[#7B1520]/15 p-4 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#7B1520]/10 flex items-center justify-center flex-shrink-0">
                      <Shield size={16} className="text-[#7B1520]" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">Verify your phone</p>
                      <p className="text-xs text-gray-500 mt-0.5">One-time verification for secure booking</p>
                    </div>
                  </div>

                  {!otpSent ? (
                    <div className="flex gap-2">
                      <div className="flex items-center flex-1 border border-gray-200 rounded-xl overflow-hidden bg-white">
                        <span className="px-3 py-3 text-sm font-bold text-gray-500 border-r border-gray-100">+91</span>
                        <input type="tel" maxLength={10} value={phone}
                          onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                          placeholder="10-digit mobile" className="flex-1 px-3 py-3 text-sm focus:outline-none" />
                      </div>
                      <button onClick={handleSendOTP} disabled={loading || phone.length !== 10}
                        className="px-4 py-3 bg-[#7B1520] hover:bg-[#9B1F2F] text-white text-sm font-bold rounded-xl disabled:opacity-40 transition-all whitespace-nowrap flex-shrink-0">
                        {loading ? "..." : "Send OTP"}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500 flex items-center gap-1.5">
                        <Check size={11} className="text-green-600" /> OTP sent to +91-{phone}
                        <button onClick={() => setOtpSent(false)} className="text-[#7B1520] font-semibold underline ml-1">Change</button>
                      </p>
                      <input type="tel" maxLength={6} value={otp}
                        onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
                        placeholder="Enter OTP"
                        className="w-full border-2 border-[#7B1520]/30 focus:border-[#7B1520] rounded-xl px-4 py-3.5 text-2xl font-black tracking-[0.6em] text-center text-[#7B1520] focus:outline-none bg-white transition-all" />
                      <button onClick={handleVerifyOTP} disabled={loading || otp.length < 4}
                        className="w-full py-3.5 bg-[#7B1520] hover:bg-[#9B1F2F] text-white font-bold text-sm rounded-xl disabled:opacity-40 transition-all">
                        {loading ? "Verifying..." : "Verify & Continue"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {!needsVerify && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-green-50 rounded-xl border border-green-200">
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <Check size={11} className="text-white" />
                  </div>
                  <p className="text-xs font-semibold text-green-700">Phone verified — {userData?.phone}</p>
                </div>
              )}

              <div>
                <p className="text-sm font-bold text-gray-800 flex items-center gap-1.5 mb-1">
                  <Users size={14} className="text-[#7B1520]" /> Participant Details ({participantCount})
                </p>
                <p className="text-[11px] text-gray-400 mb-3">
                  Don&apos;t have a participant&apos;s details? Enter &quot;NA&quot; instead of leaving it blank.
                </p>
                <div className="space-y-3">
                  {devotees.map((d, idx) => (
                    <div key={idx} className="rounded-2xl border border-gray-100 bg-gray-50 p-3.5 space-y-2.5">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Participant {idx + 1}</p>
                      <div className="grid grid-cols-2 gap-2.5">
                        <input
                          type="text" value={d.name} onChange={e => updateDevotee(idx, "name", e.target.value)}
                          placeholder="Full Name *"
                          className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7B1520]/20 focus:border-[#7B1520] bg-white placeholder:text-gray-400"
                        />
                        <input
                          type="text" value={d.gotra} onChange={e => updateDevotee(idx, "gotra", e.target.value)}
                          placeholder="Gotra *"
                          className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7B1520]/20 focus:border-[#7B1520] bg-white placeholder:text-gray-400"
                        />
                      </div>

                      {!d.showMore ? (
                        <button
                          type="button"
                          onClick={() => updateDevotee(idx, "showMore", true)}
                          className="text-[11px] text-[#7B1520] font-semibold flex items-center gap-1 hover:underline"
                        >
                          <Plus size={11} /> Add more details (age / relation)
                        </button>
                      ) : (
                        <div className="grid grid-cols-2 gap-2.5">
                          <input
                            type="number" min="0" value={d.age} onChange={e => updateDevotee(idx, "age", e.target.value)}
                            placeholder="Age"
                            className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7B1520]/20 focus:border-[#7B1520] bg-white placeholder:text-gray-400"
                          />
                          <input
                            type="text" value={d.relation} onChange={e => updateDevotee(idx, "relation", e.target.value)}
                            placeholder="Relation (e.g. Self)"
                            className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7B1520]/20 focus:border-[#7B1520] bg-white placeholder:text-gray-400"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-600 mb-1.5 block">Additional Notes <span className="font-normal text-gray-400">(optional)</span></label>
                <textarea value={extraDetailsByUser} onChange={e => setExtraDetailsByUser(e.target.value)} rows={2}
                  placeholder="Any special instructions..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7B1520]/20 focus:border-[#7B1520] bg-white resize-none" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="px-5 py-5 space-y-4">
              <div className="rounded-2xl bg-gradient-to-br from-rose-50 to-orange-50 border border-rose-100 p-4 space-y-2">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Booking Summary</p>
                <p className="font-bold text-sm text-gray-800">{templePujaName}</p>
                <p className="text-xs text-gray-500 capitalize">{TIER_LABELS[packageType] || packageType} · {participantCount} participant{participantCount > 1 ? "s" : ""}</p>
                <div className="flex flex-wrap gap-3 text-xs text-gray-500 mt-1">
                  {bookingDate && <span className="flex items-center gap-1"><Calendar size={10} /> {bookingDate}</span>}
                  {bookingTimeSlotText && <span className="flex items-center gap-1"><Clock size={10} /> {bookingTimeSlotText}</span>}
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-white">
                  <span className="text-sm font-bold text-gray-800">Total Payable</span>
                  <span className="text-xl font-black text-[#7B1520]">₹{amount}</span>
                </div>
              </div>

              <p className="text-[10px] text-gray-400 text-center leading-4">
                By placing this order you agree to our{" "}
                <a href="/terms-and-conditions" className="text-[#7B1520] underline" target="_blank">T&C</a> and{" "}
                <a href="/privacy-policy" className="text-[#7B1520] underline" target="_blank">Privacy Policy</a>
              </p>
            </div>
          )}
        </div>

        {/* ── Sticky Footer ── */}
        <div className="px-5 pt-3 pb-5 bg-white border-t border-gray-100 flex-shrink-0 space-y-2">
          {step === 1 && (
            <button
              onClick={handleStep1Next}
              disabled={loading}
              className="w-full py-4 rounded-2xl bg-[#7B1520] hover:bg-[#9B1F2F] disabled:opacity-40 text-white font-bold text-sm shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
            >
              Review &amp; Pay →
            </button>
          )}

          {step === 2 && (
            <>
              <button onClick={handlePlaceOrder} disabled={loading}
                className="w-full py-4 rounded-2xl bg-[#7B1520] hover:bg-[#9B1F2F] disabled:opacity-40 text-white font-bold text-sm shadow-md hover:shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2">
                {loading
                  ? <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="31 10" /></svg> Processing…</>
                  : `🙏 Place Order — ₹${amount}`}
              </button>
              <button onClick={() => setStep(1)} className="w-full py-1 text-xs text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center gap-1">
                <ChevronLeft size={12} /> Back
              </button>
            </>
          )}

          <ContactAdmin />
        </div>
      </div>
    </div>
  );
}
